import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { PDFDownloadLink } from '@react-pdf/renderer';
import api, { endpoints } from '../../services/api';
import ReporteDGPDF from '../../components/PDF/ReporteDGPDF';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Paper,
  Alert,
} from '@mui/material';
import {
  Download as DownloadIcon,
  ArrowBack as BackIcon,
  Warning as DGIcon,
  Search as SearchIcon,
} from '@mui/icons-material';

const ReporteDG = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    fecha_inicio: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    fecha_fin: new Date().toISOString().split('T')[0],
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams(filters).toString();
      const response = await api.get(`${endpoints.reportes.dangerousGoods}?${params}`);
      setData(response.data.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    setFilters((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const getClassColor = (clase) => {
    const colors = {
      '1': '#FF0000',
      '2': '#00FF00',
      '3': '#FF0000',
      '4': '#FF6600',
      '5': '#FFFF00',
      '6': '#FFFFFF',
      '7': '#FFFF00',
      '8': '#FFFFFF',
      '9': '#FFFFFF',
    };
    return colors[clase?.charAt(0)] || '#FFFFFF';
  };

  return (
    <Box>
      {/* Header */}
      <Card
        sx={{
          mb: 3,
          background: 'linear-gradient(135deg, #F57C00 0%, #E65100 100%)',
          color: 'white',
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <DGIcon sx={{ fontSize: 48 }} />
            <Box>
              <Typography variant="h4" fontWeight="bold">
                Dangerous Goods (DG)
              </Typography>
              <Typography sx={{ opacity: 0.9 }}>
                Registro de mercancía peligrosa transportada
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <TextField
                label="Fecha Inicio"
                type="date"
                name="fecha_inicio"
                value={filters.fecha_inicio}
                onChange={handleFilterChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Fecha Fin"
                type="date"
                name="fecha_fin"
                value={filters.fecha_fin}
                onChange={handleFilterChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Button
                variant="contained"
                onClick={fetchData}
                startIcon={<SearchIcon />}
                fullWidth
                sx={{ height: 56 }}
              >
                Buscar
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Resumen */}
      {data?.resumen && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6}>
            <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'warning.50' }}>
              <Typography variant="h3" fontWeight="bold" color="warning.main">
                {data.resumen.total_items}
              </Typography>
              <Typography color="text.secondary">Items DG</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6}>
            <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'error.50' }}>
              <Typography variant="h3" fontWeight="bold" color="error.main">
                {data.resumen.vuelos_con_dg}
              </Typography>
              <Typography color="text.secondary">Vuelos con DG</Typography>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Por Clase */}
      {data?.por_clase?.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <Box sx={{ p: 2, bgcolor: 'grey.100' }}>
            <Typography variant="h6" fontWeight="bold">Distribución por Clase</Typography>
          </Box>
          <CardContent>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              {data.por_clase.map((c) => (
                <Chip
                  key={c.clase}
                  label={`Clase ${c.clase || 'N/A'}: ${c.cantidad} items (${c.vuelos_afectados} vuelos)`}
                  sx={{
                    bgcolor: getClassColor(c.clase),
                    color: ['2', '5', '7', '8', '9'].includes(c.clase?.charAt(0)) ? 'black' : 'white',
                    fontWeight: 'bold',
                    fontSize: '0.9rem',
                    py: 2,
                  }}
                />
              ))}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Table */}
      <Card sx={{ mb: 3 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'warning.100' }}>
                <TableCell>Vuelo</TableCell>
                <TableCell>Fecha</TableCell>
                <TableCell>Destino</TableCell>
                <TableCell>Pallet</TableCell>
                <TableCell>Guía</TableCell>
                <TableCell align="center">UN</TableCell>
                <TableCell align="center">Clase</TableCell>
                <TableCell align="center">Piezas</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : data?.items?.length > 0 ? (
                data.items.map((item, idx) => (
                  <TableRow key={idx} hover>
                    <TableCell>
                      <Typography
                        component={Link}
                        to={`/vuelos/${item.awb}/detalle`}
                        sx={{ color: 'primary.main', textDecoration: 'none', fontWeight: 500 }}
                      >
                        {item.awb}
                      </Typography>
                    </TableCell>
                    <TableCell>{new Date(item.fecha).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <Chip label={item.destino} size="small" />
                    </TableCell>
                    <TableCell>{item.placa}</TableCell>
                    <TableCell>{item.guia}</TableCell>
                    <TableCell align="center">
                      <Chip label={item.un || 'N/A'} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={item.clase || 'N/A'}
                        size="small"
                        sx={{
                          bgcolor: getClassColor(item.clase),
                          color: ['2', '5', '7', '8', '9'].includes(item.clase?.charAt(0)) ? 'black' : 'white',
                          fontWeight: 'bold',
                        }}
                      />
                    </TableCell>
                    <TableCell align="center">{item.tpiezas}</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Alert severity="success" sx={{ justifyContent: 'center' }}>
                      No hay mercancía peligrosa en el rango seleccionado
                    </Alert>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Actions */}
      <Box sx={{ display: 'flex', gap: 2 }}>
        {data && (
          <PDFDownloadLink
            document={<ReporteDGPDF data={data} filters={filters} />}
            fileName={`reporte-dg-${filters.fecha_inicio}-${filters.fecha_fin}.pdf`}
            style={{ textDecoration: 'none' }}
          >
            {({ loading: pdfLoading }) => (
              <Button
                variant="contained"
                color="success"
                startIcon={pdfLoading ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
                disabled={pdfLoading}
              >
                {pdfLoading ? 'Generando...' : 'Descargar PDF'}
              </Button>
            )}
          </PDFDownloadLink>
        )}
        <Button component={Link} to="/reportes" variant="contained" color="inherit" startIcon={<BackIcon />}>
          Volver
        </Button>
      </Box>
    </Box>
  );
};

export default ReporteDG;

